import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ComposedChart,
  Bar,
  Line,
  ReferenceLine,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import type { CashflowSummary, Currency } from "../../types";
import { formatK } from "../cashflow/CashflowContext";
import {
  getGroupingBucket,
  PERIOD_GROUPING_OPTIONS,
  type PeriodGrouping,
} from "../../utils/periodGrouping";
import {
  LineChartEmptyCard,
  LineChartEmptyContent,
} from "../datamintEmptyStates";

const INCOME_EXPENSE_LEGEND = [
  { code: "Cash in", color: "#22c55e" },
  { code: "Cash out", color: "#ef4444" },
  { code: "Net", color: "#2563eb" },
] as const;

type Props = {
  summary: CashflowSummary | undefined;
  currency: Currency;
  isLoading: boolean;
};

type IncomeExpenseChartPoint = {
  period: string;
  Income: number;
  Expenses: number;
  Net: number;
};

function buildGroupedData(
  periods: CashflowSummary["periods"],
  grouping: PeriodGrouping,
): IncomeExpenseChartPoint[] {
  const sortedPeriods = [...(periods ?? [])].sort((a, b) =>
    a.month.localeCompare(b.month),
  );

  if (grouping === "month") {
    return sortedPeriods.map((period) => ({
      period: getGroupingBucket(period.month, "month").label,
      Income: period.income,
      Expenses: period.expenses,
      Net: period.net,
    }));
  }

  const grouped = new Map<string, IncomeExpenseChartPoint>();
  sortedPeriods.forEach((period) => {
    const bucket = getGroupingBucket(period.month, grouping);
    const existing = grouped.get(bucket.key);
    if (!existing) {
      grouped.set(bucket.key, {
        period: bucket.label,
        Income: period.income,
        Expenses: period.expenses,
        Net: period.net,
      });
      return;
    }

    existing.Income += period.income;
    existing.Expenses += period.expenses;
    existing.Net += period.net;
  });

  return Array.from(grouped.values());
}

export function DashboardIncomeExpensePanel({
  summary,
  currency,
  isLoading,
}: Props) {
  const chartContainerRef = useRef<HTMLDivElement | null>(null);
  const [canRenderChart, setCanRenderChart] = useState(false);
  const [dateGrouping, setDateGrouping] = useState<PeriodGrouping>("month");

  useEffect(() => {
    const element = chartContainerRef.current;
    if (!element) {
      setCanRenderChart(false);
      return;
    }

    const updateSize = () => {
      const { width, height } = element.getBoundingClientRect();
      setCanRenderChart(width > 0 && height > 0);
    };

    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(element);
    return () => observer.disconnect();
  }, [isLoading, summary?.periods?.length]);

  if (isLoading) {
    return (
      <div className="flex h-[400px] flex-col rounded-xl border border-slate-200 bg-white p-4">
        <div className="mb-4 h-9 animate-pulse rounded bg-slate-100" />
        <div className="flex-1 animate-pulse rounded-lg bg-slate-100" />
      </div>
    );
  }

  if (!summary) {
    return (
      <LineChartEmptyCard
        title="Cash In & Out"
        headerLinkLabel="Manage Accounts"
        headerLinkTo="/files-integrations"
        ctaTo="/files-integrations"
        legend={[...INCOME_EXPENSE_LEGEND]}
      />
    );
  }

  const data = buildGroupedData(summary.periods, dateGrouping);

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-4 flex min-h-[36px] flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <h3 className="text-base font-semibold text-slate-900">
          Cash In & Out
        </h3>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <Link
            to="/cashflow"
            className="font-medium text-blue-500 hover:text-slate-900 hover:underline"
          >
            View Cashflow
          </Link>
          <span className="text-slate-300">|</span>
          <div className="flex items-center gap-2">
            <span className="text-slate-500">Dates:</span>
            <select
              className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
              value={dateGrouping}
              onChange={(event) =>
                setDateGrouping(event.target.value as PeriodGrouping)
              }
              aria-label="Date grouping"
            >
              {PERIOD_GROUPING_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      <div
        ref={chartContainerRef}
        className="h-[300px] min-h-[300px] w-full min-w-0"
      >
        {data.length === 0 ? (
          <LineChartEmptyContent
            ctaTo="/files-integrations"
            legend={[...INCOME_EXPENSE_LEGEND]}
          />
        ) : canRenderChart ? (
          <ResponsiveContainer
            width="100%"
            height="100%"
            minWidth={0}
            minHeight={300}
          >
            <ComposedChart
              data={data}
              margin={{ top: 8, right: 12, bottom: 4, left: 4 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis
                dataKey="period"
                tick={{ fontSize: 11 }}
                stroke="#94a3b8"
              />
              <YAxis
                tickFormatter={(v) => formatK(Number(v), currency)}
                tick={{ fontSize: 11 }}
                stroke="#94a3b8"
              />
              <ReferenceLine y={0} stroke="#94a3b8" strokeDasharray="4 4" />
              <Tooltip
                formatter={(value: number | string, name: string) => [
                  formatK(Number(value), currency),
                  name,
                ]}
                contentStyle={{
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                  fontSize: "12px",
                }}
              />
              <Bar
                dataKey="Income"
                fill="#22c55e"
                radius={[4, 4, 0, 0]}
                barSize={14}
                name="Cash in"
              />
              <Bar
                dataKey="Expenses"
                fill="#ef4444"
                radius={[4, 4, 0, 0]}
                barSize={14}
                name="Cash out"
              />
              <Line
                type="monotone"
                dataKey="Net"
                stroke="#2563eb"
                strokeWidth={2}
                dot={false}
                name="Net Cashflow"
              />
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full w-full animate-pulse rounded-lg bg-slate-100" />
        )}
      </div>
      {data.length > 0 ? (
        <div className="flex flex-wrap justify-center gap-x-6 text-xs text-slate-600">
          <span className="inline-flex items-center text-green-500 gap-2">
            <span className="h-3.5 w-3.5 rounded-full bg-green-500" />
            Cash in{" "}
            <span className="tabular-nums font-medium">
              {formatK(summary.totalIncome ?? 0, currency)}
            </span>
          </span>
          <span className="inline-flex items-center text-red-600 gap-2">
            <span className="h-3.5 w-3.5 rounded-full bg-red-500" />
            Cash out{" "}
            <span className="tabular-nums font-medium">
              {formatK(summary.totalExpenses ?? 0, currency)}
            </span>
          </span>
          <span className="inline-flex items-center text-blue-600 gap-2">
            <span className="h-3.5 w-3.5 rounded-full bg-blue-600" />
            Net{" "}
            <span className="tabular-nums font-medium">
              {formatK(summary.netCashflow ?? 0, currency)}
            </span>
          </span>
        </div>
      ) : null}
    </div>
  );
}
